import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import checkIcon from '@/assets/images/plans/check_gold_icon.png';

type ComparisonRowConfig = {
    key: string;
    labelKey: string;
    freeDetailKey: string;
    showBasicCheck: boolean;
};

const featureRows: ComparisonRowConfig[] = [
    {
        key: 'noAds',
        labelKey: 'comparison.rows.noAds.label',
        freeDetailKey: 'comparison.rows.noAds.freeDetail',
        showBasicCheck: true,
    },
    {
        key: 'searchRange',
        labelKey: 'comparison.rows.searchRange.label',
        freeDetailKey: 'comparison.rows.searchRange.freeDetail',
        showBasicCheck: true,
    },
    {
        key: 'searchCount',
        labelKey: 'comparison.rows.searchCount.label',
        freeDetailKey: 'comparison.rows.searchCount.freeDetail',
        showBasicCheck: true,
    },
    {
        key: 'comparison',
        labelKey: 'comparison.rows.comparison.label',
        freeDetailKey: 'comparison.rows.comparison.freeDetail',
        showBasicCheck: true,
    },
    {
        key: 'calendarKeywords',
        labelKey: 'comparison.rows.calendarKeywords.label',
        freeDetailKey: 'comparison.rows.calendarKeywords.freeDetail',
        showBasicCheck: true,
    },
    {
        key: 'customLayout',
        labelKey: 'comparison.rows.customLayout.label',
        freeDetailKey: 'comparison.rows.customLayout.freeDetail',
        showBasicCheck: true,
    },
    {
        key: 'fullscreen',
        labelKey: 'comparison.rows.fullscreen.label',
        freeDetailKey: 'comparison.rows.fullscreen.freeDetail',
        showBasicCheck: true,
    },
];

function ComparisonTable() {
    const { t } = useTranslation('plans');

    const rows = useMemo(
        () =>
            featureRows.map((row) => ({
                key: row.key,
                label: t(row.labelKey),
                freeDetail: t(row.freeDetailKey),
                showBasicCheck: row.showBasicCheck,
            })),
        [t],
    );

    const checkAlt = t('comparison.checkAlt');

    return (
        <div className="flex justify-center mt-10 px-4 lg:px-40 border-gray-300 tracking-widest">
            <table className="table-auto w-full border-collapse border border-gray-300 text-xl">
                <thead>
                    <tr className="h-28 text-center">
                        <th className="w-1/3" />
                        <th className="w-1/3 bg-gray-50 border border-gray-300 py-4">
                            <h2 className="text-xl lg:text-2xl">{t('comparison.free.name')}</h2>
                            <p className="mt-2 text-sm text-gray-500">{t('comparison.free.tagline')}</p>
                        </th>
                        <th className="w-1/3 bg-gray-50 border-b-2 border-4 border-l-yellow-300 border-t-yellow-300 border-r-yellow-300 text-2xl pt-2 pb-4">
                            <p className="text-xl lg:text-2xl">{t('comparison.basic.name')}</p>
                            <p className="mt-2 text-sm text-gray-500">{t('comparison.basic.tagline')}</p>
                        </th>
                    </tr>
                </thead>
                <tbody className="text-center">
                    <tr>
                        <td className="h-24 w-1/3 text-start bg-gray-100 border border-gray-300 px-4 py-4">
                            {t('comparison.price.label')}
                        </td>
                        <td className="w-1/3 border border-gray-300 px-4 py-4">{t('comparison.price.free')}</td>
                        <td className="w-1/3 border-y-2 border-4 border-l-yellow-300 border-r-yellow-300 px-4 py-4">
                            {t('comparison.price.basic')}
                        </td>
                    </tr>
                    {rows.map((row, index) => {
                        const isLast = index === rows.length - 1;
                        const basicCellClass = isLast
                            ? 'w-1/3 border-t-2 border-4 border-l-yellow-300 border-r-yellow-300 border-b-yellow-300 px-4 py-4'
                            : 'w-1/3 border-y-2 border-4 border-l-yellow-300 border-r-yellow-300 px-4 py-4';

                        return (
                            <tr key={row.key}>
                                <td
                                    className="h-24 w-1/3 text-start bg-gray-100 border border-gray-300 px-4 py-4 whitespace-pre-line"
                                    style={{ whiteSpace: 'pre-line' }}
                                >
                                    {row.label}
                                </td>
                                <td className="w-1/3 border border-gray-300 px-4 py-4 whitespace-pre-line">
                                    {row.freeDetail}
                                </td>
                                <td className={basicCellClass}>
                                    {row.showBasicCheck ? (
                                        <img
                                            src={checkIcon}
                                            alt={checkAlt}
                                            className="w-10 h-10 mx-auto image-protection"
                                        />
                                    ) : (
                                        t(`comparison.rows.${row.key}.basicDetail`)
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default ComparisonTable;
